import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  DocumentIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase-auth';
import type { Task } from '../TaskSidePanel';

interface Activity {
  id: string;
  task_id: string;
  user_id: string;
  user_name: string;
  action_type: 'created' | 'updated' | 'commented' | 'uploaded' | 'signed' | 'completed' | 'assigned' | 'deleted';
  description: string;
  metadata?: Record<string, any>;
  ip_address: string;
  created_at: string;
}

interface ActivityTabProps {
  task: Task;
}

const actionTypeConfig = {
  created: { icon: PlusIcon, color: 'text-green-500', bgColor: 'bg-green-100' },
  updated: { icon: PencilIcon, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  commented: { icon: ChatBubbleLeftIcon, color: 'text-purple-500', bgColor: 'bg-purple-100' },
  uploaded: { icon: DocumentIcon, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  signed: { icon: CheckCircleIcon, color: 'text-green-500', bgColor: 'bg-green-100' },
  completed: { icon: CheckCircleIcon, color: 'text-emerald-500', bgColor: 'bg-emerald-100' },
  assigned: { icon: UserIcon, color: 'text-indigo-500', bgColor: 'bg-indigo-100' },
  deleted: { icon: TrashIcon, color: 'text-red-500', bgColor: 'bg-red-100' },
};

export default function ActivityTab({ task }: ActivityTabProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('all');

  useEffect(() => {
    fetchActivities();
  }, [task.id, filterType, dateRange]);

  const fetchActivities = async () => {
    try {
      let query = supabase
        .from('task_activities')
        .select(`
          *,
          user:user_id (
            id,
            name,
            email
          )
        `)
        .eq('task_id', task.id)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filterType !== 'all') {
        query = query.eq('action_type', filterType);
      }

      // Apply date range filter
      if (dateRange !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        switch (dateRange) {
          case 'today':
            startDate = new Date(now.setHours(0, 0, 0, 0));
            break;
          case 'week':
            startDate = new Date(now.setDate(now.getDate() - 7));
            break;
          case 'month':
            startDate = new Date(now.setMonth(now.getMonth() - 1));
            break;
          default:
            startDate = new Date(0);
        }
        
        query = query.gte('created_at', startDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Transform data
      const transformedActivities = (data || []).map(activity => ({
        id: activity.id,
        task_id: activity.task_id,
        user_id: activity.user_id,
        user_name: activity.user?.name || 'Unknown User',
        action_type: activity.action_type,
        description: activity.description,
        metadata: activity.metadata,
        ip_address: activity.ip_address,
        created_at: activity.created_at,
      }));
      
      setActivities(transformedActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      // Use mock data for demonstration
      setActivities(mockActivities);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockActivities: Activity[] = [
    {
      id: '1',
      task_id: task.id,
      user_id: '1',
      user_name: 'John Smith',
      action_type: 'created',
      description: 'Created task "Review Investment Policy Statement"',
      ip_address: '192.168.1.100',
      created_at: '2024-01-15T09:00:00Z',
    },
    {
      id: '2',
      task_id: task.id,
      user_id: '2',
      user_name: 'Sarah Johnson',
      action_type: 'assigned',
      description: 'Assigned task to Michael Chen',
      metadata: { assignee: 'Michael Chen' },
      ip_address: '192.168.1.101',
      created_at: '2024-01-15T09:30:00Z',
    },
    {
      id: '3',
      task_id: task.id,
      user_id: '3',
      user_name: 'Michael Chen',
      action_type: 'uploaded',
      description: 'Uploaded document "Investment_Policy_Statement.pdf"',
      metadata: { fileName: 'Investment_Policy_Statement.pdf', fileSize: 245000 },
      ip_address: '192.168.1.102',
      created_at: '2024-01-15T10:30:00Z',
    },
    {
      id: '4',
      task_id: task.id,
      user_id: '1',
      user_name: 'John Smith',
      action_type: 'commented',
      description: 'Added a comment on the document',
      ip_address: '192.168.1.100',
      created_at: '2024-01-15T11:45:00Z',
    },
    {
      id: '5',
      task_id: task.id,
      user_id: '2',
      user_name: 'Sarah Johnson',
      action_type: 'signed',
      description: 'Signed document "Investment_Policy_Statement.pdf"',
      metadata: { documentId: 'doc123' },
      ip_address: '192.168.1.101',
      created_at: '2024-01-16T14:30:00Z',
    },
    {
      id: '6',
      task_id: task.id,
      user_id: '3',
      user_name: 'Michael Chen',
      action_type: 'updated',
      description: 'Updated task priority to High',
      metadata: { oldValue: 'Medium', newValue: 'High' },
      ip_address: '192.168.1.102',
      created_at: '2024-01-16T15:00:00Z',
    },
    {
      id: '7',
      task_id: task.id,
      user_id: '1',
      user_name: 'John Smith',
      action_type: 'completed',
      description: 'Marked task as completed',
      ip_address: '192.168.1.100',
      created_at: '2024-01-16T16:00:00Z',
    },
  ];

  const handleExportCSV = () => {
    // Prepare CSV data
    const headers = ['Date/Time', 'User', 'Action', 'Description', 'IP Address'];
    const rows = activities.map(activity => [
      format(new Date(activity.created_at), 'yyyy-MM-dd HH:mm:ss'),
      activity.user_name,
      activity.action_type,
      activity.description,
      activity.ip_address,
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `task-${task.id}-activity-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getActionIcon = (actionType: Activity['action_type']) => {
    const config = actionTypeConfig[actionType];
    const Icon = config.icon;
    return <Icon className={`h-4 w-4 ${config.color}`} />;
  };

  const actionTypes = ['all', ...Object.keys(actionTypeConfig)] as const;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <ClockIcon className="h-4 w-4 mr-1" />
            Activity Timeline
          </h3>
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            Export CSV
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Action Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block w-full px-2 py-1 text-sm border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 text-gray-900"
            >
              <option value="all">All Actions</option>
              {Object.entries(actionTypeConfig).map(([type, config]) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="block w-full px-2 py-1 text-sm border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 text-gray-900"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="flex-1 overflow-y-auto p-4">
        {activities.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No activities found for the selected filters
          </p>
        ) : (
          <div className="space-y-3">
            {activities.map((activity, index) => {
              const config = actionTypeConfig[activity.action_type];
              
              return (
                <div key={activity.id} className="relative">
                  {index < activities.length - 1 && (
                    <div className="absolute top-8 left-5 w-0.5 h-full bg-gray-200" />
                  )}
                  
                  <div className="flex items-start space-x-3">
                    <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${config.bgColor} flex-shrink-0`}>
                      {getActionIcon(activity.action_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">
                            <span className="font-medium">{activity.user_name}</span>
                            {' '}
                            <span className="text-gray-600">{activity.description}</span>
                          </p>
                          
                          {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                            <div className="mt-1 text-xs text-gray-500">
                              {Object.entries(activity.metadata).map(([key, value]) => (
                                <span key={key} className="mr-3">
                                  {key}: <span className="font-medium">{value}</span>
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="mt-2 flex items-center space-x-3 text-xs text-gray-500">
                            <span>{format(new Date(activity.created_at), 'MMM d, yyyy h:mm a')}</span>
                            <span>•</span>
                            <span>IP: {activity.ip_address}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}